import { createPartner } from "@redotech/admin-sdk/rpc/schema/partners/create-partner";
import type { PartnerSummary } from "@redotech/admin-sdk/rpc/schema/partners/shared";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { useRequiredContext } from "@redotech/react-util/context";
import Building02Icon from "@redotech/redo-icons/building-02.svg";
import { clientErrorMessage } from "@redotech/rpc/errors";
import { memo, useState } from "react";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";

export const CreatePartnerModal = memo(function CreatePartnerModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (partner: PartnerSummary) => void;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const input = createPartner.input.safeParse({ name });

  const submit = async () => {
    if (!input.success) {
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      onCreated((await rpcClient.createPartner(input.data)).partner);
    } catch (err) {
      setError(clientErrorMessage(err, "Couldn't create the partner."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Create partner",
        disabled: !input.success || submitting,
        onClick: submit,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle="An integration partner, such as Zonos, that creates and manages Redo stores through the partner API."
      title="Create partner"
      TitleIcon={Building02Icon}
    >
      <RedoTextInput
        autoFocus
        errorText={error}
        fullWidth
        label="Name"
        placeholder="Zonos"
        setValue={setName}
        value={name}
      />
    </RedoModal>
  );
});
